import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Skeleton } from '@nextui-org/react';
import { CalendarDays, CheckCircle2, Radio, Trophy } from 'lucide-react';
import proTournamentInfoService, {
  ProTournamentInfo,
} from '../services/proTournamentInfoService';
import { sortByTimeline, TournamentCard } from '../components/home/ProTournamentsSection';

const ACCENT = '#3fced1';

interface SectionDef {
  key: string;
  title: string;
  icon: React.ReactNode;
  items: ProTournamentInfo[];
}

/**
 * Übersichtsseite /pdc-turniere: ALLE professionellen Turniere, gruppiert
 * nach Live / Kommend / Beendet — Klick auf eine Karte → /turniere/:slug.
 */
const PdcTournamentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<ProTournamentInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    proTournamentInfoService
      .list({ limit: 100 })
      .then((list) => {
        if (!cancelled) setTournaments(list.filter((t) => t && t.slug));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo<SectionDef[]>(() => {
    const sorted = sortByTimeline(tournaments);
    const live = sorted.filter((t) => t.status === 'LIVE');
    const upcoming = sorted.filter((t) => t.status !== 'LIVE' && t.status !== 'COMPLETED');
    const completed = sorted.filter((t) => t.status === 'COMPLETED');
    return [
      { key: 'live', title: 'Jetzt live', icon: <Radio size={20} className="text-red-400" />, items: live },
      { key: 'upcoming', title: 'Kommende Turniere', icon: <CalendarDays size={20} color={ACCENT} />, items: upcoming },
      { key: 'completed', title: 'Beendete Turniere', icon: <CheckCircle2 size={20} className="text-gray-500" />, items: completed },
    ].filter((s) => s.items.length > 0);
  }, [tournaments]);

  return (
    <>
      <Helmet>
        <title>PDC-Turniere – Alle Profi-Turniere | DartCloud</title>
        <meta
          name="description"
          content="Alle großen PDC-Turniere im Überblick: World Matchplay, World Grand Prix, World Cup of Darts & mehr – mit Terminen, Preisgeldern, Formaten und Ergebnissen."
        />
      </Helmet>

      <div className="min-h-screen bg-zinc-950 text-white">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-zinc-800/80">
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950" />
          <div
            className="absolute -top-28 left-1/3 h-[260px] w-[520px] rounded-full opacity-15 blur-3xl pointer-events-none"
            style={{ backgroundColor: ACCENT }}
          />
          <div className="relative mx-auto flex max-w-7xl items-center gap-3.5 px-4 py-10 md:px-6 md:py-12">
            <div
              className="shrink-0 rounded-xl border p-2.5"
              style={{ backgroundColor: `${ACCENT}14`, borderColor: `${ACCENT}33` }}
            >
              <Trophy size={22} color={ACCENT} />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
                PDC-Turniere
              </h1>
              <p className="mt-0.5 text-sm text-gray-400">
                Die großen Turniere der Darts-Welt – Termine, Preisgelder, Formate &amp; Ergebnisse.
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl space-y-10 px-4 py-8 md:px-6 md:py-10">
          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
                  <Skeleton className="aspect-video w-full bg-zinc-800" />
                  <div className="space-y-2 p-4">
                    <Skeleton className="h-5 w-3/4 rounded bg-zinc-800" />
                    <Skeleton className="h-4 w-1/2 rounded bg-zinc-800" />
                  </div>
                </div>
              ))}
            </div>
          ) : sections.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-800 py-16 text-center">
              <Trophy size={28} color={ACCENT} className="mx-auto mb-4 opacity-80" />
              <p className="text-lg font-semibold text-white">Keine Turniere gefunden</p>
              <p className="mt-1 text-sm text-gray-500">Schau bald wieder vorbei!</p>
            </div>
          ) : (
            sections.map((section) => (
              <section key={section.key} aria-label={section.title}>
                <div className="mb-5 flex items-center gap-2.5">
                  {section.icon}
                  <h2 className="text-lg font-bold text-white md:text-xl">{section.title}</h2>
                  <span className="rounded-full bg-zinc-900 border border-zinc-800 px-2 py-0.5 text-xs text-gray-400">
                    {section.items.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {section.items.map((tournament) => (
                    <TournamentCard
                      key={tournament.slug || tournament.id}
                      tournament={tournament}
                      fluid
                      onClick={() => navigate(`/turniere/${tournament.slug}`)}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default PdcTournamentsPage;
